/**
 * @ngdoc service
 * @name categoryApiService
 * @module flowingly.services
 *
 * @description A service responsible solely for fetching categories data
 */

import { Services } from '@Shared.Angular/@types/services';
import angular, { IHttpService } from 'angular';
import { CategoryDeleteWarningStatus } from './flowingly.constants';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { sortCategoriesNaturally } from '@Shared.Angular/flowingly.helper/naturalSort';

export interface ISubCategoryFlowModel {
  subCategoryName: string;
  flowModelCount: number;
}

export interface ICategoryDeleteWarning {
  subCategoryFlowModels: ISubCategoryFlowModel[];
  flowModelCount: number;
  procedureCount: number;
  status: CategoryDeleteWarningStatus;
}

export interface IDeleteCategoryRequest {
  categoryId: Guid;
  replaceCategoryId?: Guid;
}

export default class CategoryApiService {
  constructor(
    private $q: ng.IQService,
    private $http: angular.IHttpService,
    private APP_CONFIG: Services.APP_CONFIG
  ) {}

  public cachedCategories: ICategoryDetail[];
  private categoriesPromise: ng.IPromise<ICategoryDetail[]>;

  addCategory(category: ICategoryDetail) {
    return this.$http.post<Guid>(
      this.APP_CONFIG.apiBaseUrl + `categories/`,
      category
    );
  }

  editCategory(category: ICategoryDetail) {
    return this.$http.put<Guid>(
      this.APP_CONFIG.apiBaseUrl + `categories/`,
      category
    );
  }

  deleteCategory(categoryId: Guid, replaceCategoryId?: Guid) {
    return this.$http<object>({
      url: this.APP_CONFIG.apiBaseUrl + 'categories/',
      method: 'DELETE',
      data: { categoryId, replaceCategoryId } as IDeleteCategoryRequest,
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    });
  }

  getCategories(noSpinner = false) {
    return this.$http
      .get<ICategoryDetail[]>(this.APP_CONFIG.apiBaseUrl + 'categories', {
        noSpinner: noSpinner || false
      })
      .then((response) => {
        const sortedCategories = sortCategoriesNaturally(response.data);

        this.cachedCategories = angular.copy(sortedCategories);
        this.categoriesPromise = this.$q.resolve(this.cachedCategories);
        return sortedCategories;
      });
  }

  getCachedCategories(noSpinner = false) {
    if (!this.categoriesPromise) {
      this.categoriesPromise = this.getCategories(noSpinner);
    }
    return this.categoriesPromise;
  }

  getSubCategoryWarning(itemId) {
    return this.$http
      .get<ICategoryDeleteWarning>(
        this.APP_CONFIG.apiBaseUrl +
          'categories/deletewarnings?categoryId=' +
          itemId
      )
      .then((res) => {
        return res.data;
      });
  }

  findCategory(
    targetCategoryId: Guid,
    categories: ICategoryDetail[]
  ): ICategoryDetail | null {
    for (const category of categories) {
      if (category.id === targetCategoryId) {
        return category;
      }
      if (category.subCategories && category.subCategories.length > 0) {
        const subCategoryResult = this.findCategory(
          targetCategoryId,
          category.subCategories
        );
        if (subCategoryResult) {
          return subCategoryResult;
        }
      }
    }
    return null;
  }
}

angular
  .module('flowingly.services')
  .factory('categoryApiService', [
    '$q',
    '$http',
    'APP_CONFIG',
    ($q: ng.IQService, $http: IHttpService, APP_CONFIG: Services.APP_CONFIG) =>
      new CategoryApiService($q, $http, APP_CONFIG)
  ]);

export type CategoryApiServiceType = InstanceType<typeof CategoryApiService>;
